import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersistence from 'vuex-persist'
import { Appearance } from '@/models/UserPreference.js';
import { initAuthStore } from './initAuthStore';
import * as map from 'lodash/map';
import _extend from 'lodash/extend';
import _omit from 'lodash/omit';
import { adminRoles, depotRoles, alarmCenterRoles, roles } from '@/models/Role';

Vue.use(Vuex);

/**
 * Everything the SPA holds is mirrored into localStorage, except the two lists
 * that must never be answered from a cache (RR-330).
 *
 * `required_actions` decides whether the router pins the user to a page, and
 * `announcements` is what the banner shows. Both are only true as of the last
 * find-by-token: a copy left in localStorage would keep blocking someone who
 * has already fixed the problem, keep letting through someone for whom it was
 * just raised, and re-show announcements they dismissed on another device.
 * They come back on the next bootstrap, which is cheap — they ride along on a
 * request the SPA makes anyway.
 *
 * `noticesFresh` is forced false for the same reason: it is what tells the
 * guard that these lists came from the server in this page load.
 */
const VOLATILE_USER_KEYS = ['required_actions', 'announcements'];

/**
 * Goes through the model's own toJSON() first, so what gets stored is still
 * exactly what JSON.stringify would have produced — minus the two keys.
 * After a reload state.user is a plain object and there is no toJSON().
 */
const persistableUser = user => {
  if (!user) return user;

  return _omit(typeof user.toJSON === 'function' ? user.toJSON() : user, VOLATILE_USER_KEYS);
};

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  reducer: state => ({
    ...state,
    noticesFresh: false,
    user: persistableUser(state.user)
  })
})

export default (vueAuth) => {

  return new Vuex.Store({
    plugins: [vuexLocal.plugin],
    state: {
      user: null,
      vehicle_object_type: null,
      // Whether required_actions and announcements on state.user came from
      // find-by-token in this page load rather than from localStorage (RR-330).
      noticesFresh: false,
    },
    mutations: {
      setUser(state, user) {
        state.user = user;
        state.noticesFresh = true;

        return user;
      },
      updateUser(state, user) {
        state.user.assign(user);

        return user;
      },
      clearUser(state, user) {
        state.user = null
        state.noticesFresh = false;

        return user;
      },
      updateUserPreference(state, userPreference) {
        return _extend(state.user.preference, userPreference);
      },
      dismissAnnouncement(state, announcementId) {
        // Mirrors the row written to announcement_user, so the banner does not
        // reappear before the next find-by-token (RR-330).
        if (state.user?.announcements) {
          state.user.announcements = state.user.announcements.filter(
            announcement => announcement.id !== announcementId
          );
        }
      },
    },
    modules: {
      auth: initAuthStore(vueAuth)
    },
    actions: {
      clearUser({ commit, state }) {
        commit('clearUser')
      },
      setUser({ commit, state }, user) {
        commit('setUser', user)
      },
      updateUser({ commit, state }, user) {
        commit('updateUser', user)
      },
      setAppearance({ commit, state }, appearance) {

        if (state.user) {
          commit('updateUserPreference', {
            appearance: appearance
          })
        }

        document.body.className = appearance;
      },
      updateUserPreference({ commit, state }, userPreference) {
        commit('updateUserPreference', userPreference);
      },
      dismissAnnouncement({ commit }, announcementId) {
        commit('dismissAnnouncement', announcementId);
      },
    },
    getters: {
      vehicleObjectType: state => {
        return state.vehicle_object_type;
      },
      user: state => {
        return state.user;
      },
      sessionReady: (state, getters) =>
        getters.isAuthenticated && state.noticesFresh,
      /**
       * Empty until find-by-token has answered in this page load (RR-330).
       *
       * The router guard and the notice banner both read this rather than
       * state.user.required_actions, so neither can act on a value that
       * survived in localStorage from an earlier session.
       */
      requiredActions: state => {
        return state.noticesFresh ? state.user?.required_actions ?? [] : [];
      },
      announcements: state => {
        return state.noticesFresh ? state.user?.announcements ?? [] : [];
      },
      /**
       * The action that should pin the user to a single route, or null. Mirrors
       * RequiredActionService::blockingFor(): an action that declares itself
       * blocking without a route cannot block, and the backend already
       * downgrades those, so this is a second line of defence.
       */
      blockingRequiredAction: (state, getters) => {
        return getters.requiredActions.find(action => action.blocking && action.route) ?? null;
      },
      userPreference: state => {
        return state.user?.preference;
      },
      hasFrontendAccess: state => {
        return state.user?.has_frontend_access;
      },
      contractColumns: state => {
        return state.user?.preference?.contract_columns;
      },
      contractColumnDict: state => {
        const contract_columns = state.user?.preference?.contract_columns;
        const dict = {};

        if (!contract_columns) return dict;

        for (var i = 0; i < contract_columns.length; ++i)
          if (contract_columns[i] !== undefined) dict[contract_columns[i]] = true;

        return dict;
      },
      appearance: state => {
        return state.user?.preference ? state.user.preference.appearance : Appearance.DEFAULT;
      },
      client: state => {
        return state.user?.client;
      },
      contractTemplates: state => {
        return state.user?.client ? state.user.client.contract_templates : [];
      },
      clients: state => {
        return state.user?.clients ? state.user.clients : [];
      },
      clientsOptions: (state, getters) => {
        if (!getters.user || !getters.user.clients) return [];

        return getters.user.clients.map(item => ({ value: item.id, text: item.company.name }));
      },
      contractTemplateOptions: (state, getters) => {
        if (!getters.contractTemplates) return [];

        return getters.contractTemplates.map(item => ({ value: item.id, text: item.name }));
      },
      contractTemplate: (state, getters) => {
        const preference = getters.userPreference;
        const contractTemplates = getters.contractTemplates;

        if (!contractTemplates) return null;

        if (preference?.contract_template_id) {
          return contractTemplates.find(item => item.id == preference.contract_template_id)
        } else {
          // return contractTemplates.find(item => item != null)
        }
      },
      isAdmin: state => {
        const userRoles = map(state.user.roles, 'type')
        return userRoles.some(r => adminRoles.includes(r))
      },
      hasDepotRole: state => {
        const userRoles = map(state.user.roles, 'type')
        return userRoles.some(r => depotRoles.includes(r))
      },
      hasAlarmCenterRole: state => {
        const userRoles = map(state.user.roles, 'type')
        return userRoles.some(r => alarmCenterRoles.includes(r))
      },
      hasApiRole: state => {
        const userRoles = map(state.user.roles, 'type')
        return userRoles.some(r => r === roles.ROLE_TYPE_API_USER)
      },
      isSenior: state => {
        const userRoles = map(state.user.roles, 'type')
        return userRoles.some(r => r === roles.ROLE_SENIOR)
      },
    }
  })
}
